import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Cloud, Loader2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCloudConnections } from "@/hooks/useCloudConnections";
import { useToast } from "@/hooks/use-toast";
import { useSearchParams } from "react-router-dom";

// Google Docs icon component (official style - blue document with lines)
const GoogleDriveIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path fill="#4285F4" d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <path fill="#1A73E8" d="M14 2v6h6" />
    <path fill="#fff" d="M8 12h8M8 15h8M8 18h5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

// Dropbox icon component (official blue box style)
const DropboxIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path fill="#0061FF" d="M6 2l6 4-6 4-6-4 6-4zm12 0l6 4-6 4-6-4 6-4zM0 14l6-4 6 4-6 4-6-4zm18-4l6 4-6 4-6-4 6-4zM6 17l6-4 6 4-6 4-6-4z" />
  </svg>
);

interface CloudImportButtonsProps {
  onGoogleClick: () => void;
  onDropboxClick: () => void;
}

export function CloudImportButtons({ onGoogleClick, onDropboxClick }: CloudImportButtonsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isConnected, connectGoogle, connectDropbox, loading, refresh } = useCloudConnections();

  // Handle OAuth callback
  useEffect(() => {
    const connected = searchParams.get("connected");
    const error = searchParams.get("error");

    if (connected) {
      toast({
        title: t("cloudImport.connectionSuccess"),
        description: t(`cloudImport.connectedTo.${connected}`),
      });
      refresh();
      // Clean up URL
      searchParams.delete("connected");
      setSearchParams(searchParams, { replace: true });
    }

    if (error) {
      toast({
        title: t("cloudImport.connectionError"),
        description: error,
        variant: "destructive",
      });
      searchParams.delete("error");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast, t, refresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const googleConnected = isConnected("google");
  const dropboxConnected = isConnected("dropbox");

  return (
    <div className="flex flex-col gap-4 py-4">
      <p className="text-sm text-muted-foreground text-center">
        {t("cloudImport.description")}
      </p>

      <div className="grid gap-3">
        {/* Google Drive */}
        <div className="flex items-center gap-3 p-4 border-2 border-black rounded-xl bg-white shadow-[4px_4px_0_#1A1A1A]">
          <div className="p-2 rounded-lg border-2 border-black bg-blue-50">
            <GoogleDriveIcon />
          </div>
          <div className="flex-1">
            <h4 className="font-bold">{t("cloudImport.googleDocs.name")}</h4>
            <p className="text-sm font-medium text-muted-foreground">
              {googleConnected 
                ? t("cloudImport.connected") 
                : t("cloudImport.googleDocs.description")}
            </p>
          </div>
          {googleConnected ? (
            <Button onClick={onGoogleClick} className="border-2 border-black shadow-[2px_2px_0_#1A1A1A]">
              <Cloud className="h-4 w-4 mr-2" />
              {t("cloudImport.browse")}
            </Button>
          ) : (
            <Button variant="outline" onClick={connectGoogle} className="border-2 border-black bg-white hover:bg-gray-100">
              {t("cloudImport.connect")}
            </Button>
          )}
        </div>

        {/* Dropbox */}
        <div className="flex items-center gap-3 p-4 border-2 border-black rounded-xl bg-white shadow-[4px_4px_0_#1A1A1A]">
          <div className="p-2 rounded-lg border-2 border-black bg-blue-50">
            <DropboxIcon />
          </div>
          <div className="flex-1">
            <h4 className="font-bold">{t("cloudImport.dropbox.name")}</h4>
            <p className="text-sm font-medium text-muted-foreground">
              {dropboxConnected 
                ? t("cloudImport.connected") 
                : t("cloudImport.dropbox.description")}
            </p>
          </div>
          {dropboxConnected ? (
            <Button onClick={onDropboxClick} className="border-2 border-black shadow-[2px_2px_0_#1A1A1A]">
              <Cloud className="h-4 w-4 mr-2" />
              {t("cloudImport.browse")}
            </Button>
          ) : (
            <Button variant="outline" onClick={connectDropbox} className="border-2 border-black bg-white hover:bg-gray-100">
              {t("cloudImport.connect")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
